import axios from 'axios';
import Cookies from 'js-cookie';

// Create axios instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      Cookies.remove('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  // Google OAuth authentication
  authenticateWithGoogle: async (googleToken: string) => {
    const formData = new FormData();
    formData.append('google_token', googleToken);
    
    const response = await api.post('/auth/google', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Google OAuth with authorization code
  authenticateWithCode: async (code: string) => {
    const formData = new FormData();
    formData.append('code', code);
    
    const response = await api.post('/auth/google/callback', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get Google OAuth URL
  getGoogleAuthUrl: async () => {
    const response = await api.get('/auth/google/url');
    return response.data.auth_url;
  },

  // Get user profile
  getUserProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },

  // Get user usage statistics
  getUserUsage: async () => {
    const response = await api.get('/user/usage');
    return response.data;
  },
};

export const fileService = {
  // Upload file for analysis
  uploadFile: async (
    file: File, 
    additionalQuestions: string = '', 
    patientAge: string, 
    patientGender: string
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('additional_questions', additionalQuestions);
    formData.append('patient_age', patientAge);
    formData.append('patient_gender', patientGender);

    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for file upload
    });
    return response.data;
  },

  // Get processing status
  getProcessingStatus: async () => {
    const response = await api.get('/status');
    console.log('[API] Status response:', response.data);
    return response.data;
  },
};

export const adminService = {
  // Get application logs
  getLogs: async () => {
    const response = await api.get('/admin/logs');
    return response.data;
  },

  // Get system statistics
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Get WebSocket statistics
  getWebSocketStats: async () => {
    const response = await api.get('/ws/stats');
    return response.data;
  },
};

export const healthService = {
  // Health check
  checkHealth: async () => {
    const response = await api.get('/health');
    return response.data;
  },

  // Basic ping
  ping: async () => {
    const response = await api.get('/');
    return response.data;
  },
};

// Export default api instance
export default api;










